import { createContext, useCallback, useContext, useRef, useState } from "react";
import { CheckCircle } from "react-feather";

// Optional tappable action (e.g. "Undo"), given longer on screen so it can
// actually be pressed. Mirrors the RN ToastProvider.
type ToastAction = { label: string; onClick: () => void };
type ToastContextValue = { show: (message: string, action?: ToastAction) => void };

const ToastContext = createContext<ToastContextValue>({ show: () => {} });

export const useToast = () => useContext(ToastContext);

const VISIBLE_MS = 2500;
const VISIBLE_WITH_ACTION_MS = 5000;

// App-root toast host: a fixed, bottom-centre dark chip that overlays everything
// (above modals) so a confirmation stays legible on any background — the light
// `bg-surface` chip it replaced washed out in light mode. Deliberately dark in
// both themes, matching the RN toast (always stone-800).
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<ToastAction | null>(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setVisible(false);
    // Unmount only after the fade-out so the exit transition plays.
    clearTimer.current = setTimeout(() => {
      setMessage(null);
      setAction(null);
    }, 250);
  }, []);

  const show = useCallback(
    (msg: string, act?: ToastAction) => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (clearTimer.current) clearTimeout(clearTimer.current);
      setMessage(msg);
      setAction(act ?? null);
      // Mount hidden, then flip to visible next frame so opacity animates in.
      setVisible(false);
      requestAnimationFrame(() => setVisible(true));
      hideTimer.current = setTimeout(
        hide,
        act ? VISIBLE_WITH_ACTION_MS : VISIBLE_MS,
      );
    },
    [hide],
  );

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {message && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed left-1/2 -translate-x-1/2 bottom-6 z-[9999] transition-opacity duration-200 ${
            visible ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="flex items-center gap-2 bg-stone-800 rounded-2xl px-4 py-3 shadow-lg ring-1 ring-white/10 max-w-[90vw]">
            <CheckCircle className="w-[18px] h-[18px] shrink-0 text-terracotta" />
            <span className="text-sm font-semibold text-white">{message}</span>
            {action && (
              <button
                type="button"
                onClick={() => {
                  action.onClick();
                  hide();
                }}
                className="ml-1 shrink-0 text-sm font-bold text-terracotta cursor-pointer"
              >
                {action.label}
              </button>
            )}
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}
